import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react';
import { Button, Image, Grid, Popup, Icon } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';

import { useSharedData } from '../../hooks/core/useZoneHooks';

import { LitGroupManagementEnh, BookManagementEnh, CharacterManagementEnh, VariationManagementEnh } from '../management';
import { AuthStdFooter } from "../../../utils/auth-std-footer"
// Phase 4a §8.1 — compact voice-readiness strip anchored in the worksheet
// header. Renders only when a book is selected; its internal thunks carry
// their own 30s TTL so the remounts caused by tab switches here are benign.
import BookVoiceStatsStrip from '../../../components/voiceStats/BookVoiceStatsStrip';
// Phase 4c §6c — Dry Resolve diagnostic drawer, mounted directly beneath
// BookVoiceStatsStrip so BCs can trust-but-verify the variation fallback
// chain before burning ElevenLabs credits on a real render.
import DryResolvePanel from '../../../components/voiceRendering/DryResolvePanel';
// Phase 4a follow-up — the worksheet is the first page a BC lands on for a
// given book, so it also owns the "fetch the full book data set" dispatch.
// Without this, state.user.storyText.processedVersionItems stays empty,
// and ScriptVersionSelector (Phase 4a §7) renders its disabled empty state
// even when a v0 ProcessedItem exists in Neo4j. See PHASE_4A_COMPLETE.md
// "Known deferrals / follow-ups" for the full defect brief.
import {
  fetchFullBookDataSet,
  clearStory
} from '../../../slices/storyEditorSlice';

import '../../styles/bookWorksheet.css';

// Note: The "Voice Actor Market" header button was moved to the Book Creator
// Landing page's Quick Actions (as "Manage Voiced Opportunities") per UX
// consolidation — see BookCreatorLandingPage.jsx.



// Constants for tab indices
const TAB_INDEX = {
  LIT_GROUP: 0,
  BOOKS: 1,
  CHARACTERS: 2,
  VARIATIONS: 3
};

// Create a context for tab navigation
export const TabNavigationContext = createContext();

// Hook to use tab navigation
export const useTabNavigation = () => {
  const context = useContext(TabNavigationContext);
  if (!context) {
    throw new Error('useTabNavigation must be used within a TabNavigationProvider');
  }
  return context;
};

/**
 * BookWorksheetPageEnh component
 * Enhanced version of BookWorksheetPage that uses the enhanced components
 * @returns {JSX.Element} BookWorksheetPageEnh component
 */
const BookWorksheetPageEnh = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sharedData = useSharedData();
  const dispatch = useDispatch();

  // Phase 4a follow-up — selectors for the storyEditor fetch-guard. We use
  // fetchFullBookDataSetStatus === 'idle' to decide whether to dispatch, so
  // navigating here from a page that already populated the slice (e.g.
  // AIFileEditor) doesn't trigger a second fetch.
  const userId = useSelector((s) => s.user?.profile?.data?.userId);
  const fullBookStatus = useSelector(
    (s) => s.user?.storyText?.fetchFullBookDataSetStatus
  );

  // Extract data from new hook
  const {
    litGroups,
    books,
    selections
  } = sharedData;

  // Map to old interface for compatibility
  const litGroupsArray = Array.isArray(litGroups) ? litGroups : [];
  const booksArray = Array.isArray(books) ? books : [];
  const selectionsObj = selections || {};

  const selectedLitGroup = litGroupsArray.find(lg => lg.uuid === selectionsObj.litGroupId) || null;
  const selectedBook = booksArray.find(b => b.uuid === selectionsObj.bookId) || null;
  const litGroupId = selectionsObj.litGroupId;
  const bookId = selectionsObj.bookId;

  // For now, we'll use dummy functions for persistSelections since it's not in the new architecture
  const persistSelections = true;
  const setPersistSelections = () => { };

  // Set persistSelections to true when the component mounts
  useEffect(() => {
    console.log('BookWorksheetPageEnh: Setting persistSelections to true on mount');
    setPersistSelections(true);

    // Initialize data fetching
    sharedData.actions.fetchLitGroups();

    // Clean up function - only clear selections when truly leaving the worksheet
    return () => {
      console.log('BookWorksheetPageEnh: Component unmounting - worksheet is being closed');
    };
  }, []); // Remove dependencies to prevent infinite loop

  // Auto-select first literary group when lit groups are loaded
  useEffect(() => {
    console.log('BookWorksheetPageEnh: Auto-selection check - litGroupsArray.length:', litGroupsArray.length, 'litGroupId:', litGroupId);
    console.log('BookWorksheetPageEnh: litGroupsArray:', litGroupsArray);
    if (litGroupsArray.length > 0 && !litGroupId) {
      console.log('BookWorksheetPageEnh: Auto-selecting first literary group:', litGroupsArray[0].uuid);
      sharedData.actions.setLitGroupSelection(litGroupsArray[0].uuid);
      // Fetch books for the selected literary group
      sharedData.actions.fetchBooks(litGroupsArray[0].uuid);
    }
  }, [litGroupsArray, litGroupId]); // Remove sharedData.actions from dependencies

  // Handle tab button click - memoized for stable context value
  const handleTabClick = useCallback((tabIndex) => {
    // Check if trying to navigate to Characters or Variations tabs without a book selected
    if ((tabIndex === TAB_INDEX.CHARACTERS || tabIndex === TAB_INDEX.VARIATIONS) && !bookId) {
      // Don't allow navigation to these tabs if no book is selected
      return;
    }

    setActiveIndex(tabIndex);
    setPersistSelections(true);
  }, [bookId, setPersistSelections]);

  // Add effect to redirect if needed
  useEffect(() => {
    // If on Characters or Variations tab but no book is selected, redirect to Books tab
    if ((activeIndex === TAB_INDEX.CHARACTERS || activeIndex === TAB_INDEX.VARIATIONS) && !bookId) {
      setActiveIndex(TAB_INDEX.BOOKS); // Books tab index
    }
  }, [activeIndex, bookId]);

  // Phase 4a follow-up — dispatch fetchFullBookDataSet on mount / bookId change.
  //
  // The worksheet is the first page a Book Creator lands on for a given book,
  // so it owns the story-text fetch. This drives:
  //   - state.user.storyText.fullBookDataSet.processedVersionItems
  //     → ScriptVersionSelector (Phase 4a §7) renders real options instead
  //       of its "No imported script versions yet." empty state.
  //   - state.user.storyText.activeFullBookTreeData
  //     → downstream character / variation management components that read
  //       from the book tree without dispatching their own fetch.
  //
  // Guarded on fetchFullBookDataSetStatus === 'idle' so navigating here from
  // a page that already populated the slice (e.g. AIFileEditor) is a no-op.
  // clearStory() flushes the default "Seriously, you must select a story…"
  // placeholder so UIs keyed off activeStoryJson.content don't flash it.
  useEffect(() => {
    if (!bookId || !userId) return;
    if (fullBookStatus && fullBookStatus !== 'idle') return;
    // eslint-disable-next-line no-console
    console.log(
      `[BookWorksheetPageEnh] dispatching fetchFullBookDataSet on bookId=${bookId} ` +
      `(fullBookStatus=${fullBookStatus || 'idle'})`
    );
    dispatch(clearStory());
    dispatch(fetchFullBookDataSet({ userId, bookUuid: bookId }));
  }, [dispatch, bookId, userId, fullBookStatus]);

  // Add CSS styles for the tab navigation
  const styles = {
    tabNavigation: {
      display: 'flex',
      marginBottom: '1rem',
      borderBottom: '1px solid #ddd',
      padding: '0 1rem'
    },
    tabButton: {
      marginRight: '0.5rem',
      borderRadius: '0.28571429rem 0.28571429rem 0 0',
      borderBottom: 'none'
    },
    activeTabButton: {
      borderBottom: '2px solid #2185d0'
    },
    tabContent: {
      border: '1px solid #ddd',
      borderTop: 'none',
      padding: '1rem',
      marginTop: '-1px'
    }
  };

  // Phase 2: Memoize the navigation context value to prevent unnecessary re-renders
  const navigationContextValue = useMemo(() => ({
    handleTabClick,
    TAB_INDEX
  }), [handleTabClick]);

  return (
    <TabNavigationContext.Provider value={navigationContextValue}>
      <div className="bw-book-worksheet">
        <div className="bw-book-worksheet-header">
          <Grid verticalAlign="middle">
            <Grid.Row >
              <Grid.Column width={4}>
                <div className="header-left">
                  <Image src="/bookStack.png" size="tiny" />
                  <h1>Book Creator Worksheet</h1>
                </div>
              </Grid.Column>
              <Grid.Column width={12} textAlign="left">
                {selectedLitGroup && (
                  <div className="selected-entity-info header-middle">
                    <span>Selected Literary Group: <strong>{selectedLitGroup.name}</strong></span>
                    {selectedBook && (
                      <span> | Selected Book: <strong>{selectedBook.name}</strong></span>
                    )}
                  </div>
                )}
              </Grid.Column>
            </Grid.Row>

          </Grid>
          {/*
            Phase 4a §8.1 — compact voice-readiness strip. Lives inside the
            worksheet header so it's visible on every tab once a book is
            selected. We deliberately omit it on the "no book yet" case
            rather than rendering a zero-state inside the header; the
            VoiceCloningPipeline page remains the canonical deep view.
          */}
          {bookId && (
            <BookVoiceStatsStrip
              bookId={bookId}
              variant="compact"
              className="bw-voice-stats-strip"
            />
          )}
          {bookId && (
            <DryResolvePanel
              bookId={bookId}
              className="bw-dry-resolve-panel"
            />
          )}
        </div>

        {/* Custom tab navigation */}
        <div style={styles.tabNavigation}>
          <Button
            primary={activeIndex === TAB_INDEX.LIT_GROUP}
            onClick={() => handleTabClick(TAB_INDEX.LIT_GROUP)}
            icon
            labelPosition="left"
            style={{
              ...styles.tabButton,
              ...(activeIndex === TAB_INDEX.LIT_GROUP ? styles.activeTabButton : {})
            }}
          >
            <Icon name="users" />
            Literary Group
          </Button>
          <Button
            primary={activeIndex === TAB_INDEX.BOOKS}
            onClick={() => handleTabClick(TAB_INDEX.BOOKS)}
            icon
            labelPosition="left"
            style={{
              ...styles.tabButton,
              ...(activeIndex === TAB_INDEX.BOOKS ? styles.activeTabButton : {})
            }}
          >
            <Icon name="book" />
            Books
          </Button>
          <Popup
            trigger={
              <span style={{ display: 'inline-block' }}>
                <Button
                  primary={activeIndex === TAB_INDEX.CHARACTERS}
                  onClick={() => handleTabClick(TAB_INDEX.CHARACTERS)}
                  disabled={!bookId}
                  icon
                  labelPosition="left"
                  style={{
                    ...styles.tabButton,
                    ...(activeIndex === TAB_INDEX.CHARACTERS ? styles.activeTabButton : {})
                  }}
                >
                  <Icon name="user" />
                  Characters
                </Button>
              </span>
            }
            content="Select a book first to manage characters"
            position="top center"
            disabled={!!bookId}
          />
          <Popup
            trigger={
              <span style={{ display: 'inline-block' }}>
                <Button
                  primary={activeIndex === TAB_INDEX.VARIATIONS}
                  onClick={() => handleTabClick(TAB_INDEX.VARIATIONS)}
                  disabled={!bookId}
                  icon
                  labelPosition="left"
                  style={{
                    ...styles.tabButton,
                    ...(activeIndex === TAB_INDEX.VARIATIONS ? styles.activeTabButton : {})
                  }}
                >
                  <Icon name="microphone" />
                  Variations
                </Button>
              </span>
            }
            content="Select a book first to manage variations"
            position="top center"
            disabled={!!bookId}
          />
        </div>

        {/* Persistent containers */}
        <div className="bw-neg-margin-top" style={styles.tabContent}>
          <div style={{ display: activeIndex === TAB_INDEX.LIT_GROUP ? 'block' : 'none' }}>
            <LitGroupManagementEnh />
          </div>
          <div style={{ display: activeIndex === TAB_INDEX.BOOKS ? 'block' : 'none' }}>
            <BookManagementEnh />
          </div>
          <div style={{ display: activeIndex === TAB_INDEX.CHARACTERS ? 'block' : 'none' }}>
            <CharacterManagementEnh />
          </div>
          <div style={{ display: activeIndex === TAB_INDEX.VARIATIONS ? 'block' : 'none' }}>
            <VariationManagementEnh />
          </div>
        </div>
        {AuthStdFooter()}

        {/* Always-mounted components for data fetching (hidden) */}
        {/* <div style={{ display: 'none' }}>
          Mount CharacterManagement to ensure character fetching happens
          {bookId && <CharacterManagementEnh />}
        </div> */}
      </div>
    </TabNavigationContext.Provider>
  );
};

export default BookWorksheetPageEnh;
